'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router-dom';
import { trackPageView } from '../../../mixpanel/mixpanel-client';
import type { UsePageTrackingOptions } from '../types';
import { getHubPageName } from '../utils';

/**
 * Global tracking state - shared across all hook instances
 * Prevents duplicate tracking when hook is mounted in multiple layouts
 */
let lastTrackedPath = '';
let lastTrackedTime = 0;
const DEDUP_WINDOW_MS = 100;

/**
 * Hook to handle page view tracking with standardized page names.
 *
 * This hook automatically tracks page views on route changes with:
 * - Standardized `page_name` (e.g., 'hub_overview', 'hub_program_dashboard')
 * - `hub_language` property for the user's current language
 * - Redirect prevention: Only tracks when navigation is idle to avoid
 *   tracking intermediate routes during redirect chains
 * - Global deduplication: Prevents duplicate tracking when hook is mounted
 *   in multiple layouts simultaneously
 *
 * @param options - Optional configuration
 * @param options.pageName - Override automatic page name detection
 * @param options.skip - Skip tracking for this page
 *
 * @example
 * // Basic usage - automatically tracks all route changes
 * const Layout = () => {
 *   usePageTracking();
 *   return <Outlet />;
 * };
 *
 * @example
 * // Override page name for special cases
 * const AffiliateWelcome = () => {
 *   usePageTracking({ pageName: HubPageName.AFFILIATE_WELCOME });
 *   return <WelcomeContent />;
 * };
 */
export const usePageTracking = (options: UsePageTrackingOptions = {}) => {
  const location = useLocation();
  const navigation = useNavigation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (options.skip) {
      return;
    }

    // Only track when navigation is idle (not during redirects/loading)
    // This prevents tracking intermediate routes in redirect chains
    if (navigation.state !== 'idle') {
      return;
    }

    const currentPath = location.pathname;
    const now = Date.now();

    // Prevent duplicate tracking for same path within dedup window
    // Exception: If pageName is explicitly provided, always track (allows
    // components to override automatic tracking for the same path)
    const isDuplicate =
      lastTrackedPath === currentPath &&
      now - lastTrackedTime < DEDUP_WINDOW_MS;

    if (isDuplicate && !options.pageName) {
      return;
    }

    lastTrackedPath = currentPath;
    lastTrackedTime = now;

    // Resolve page name from pathname or use override
    const pageName = options.pageName || getHubPageName(currentPath);

    // Track the page view with standardized properties
    trackPageView({
      page_name: pageName,
      hub_language: i18n.language,
    });
  }, [
    location.pathname,
    navigation.state,
    options.pageName,
    options.skip,
    i18n.language,
  ]);

  return {
    currentPageName: options.pageName || getHubPageName(location.pathname),
  };
};

/**
 * Reset global tracking state (useful for testing)
 * @internal
 */
export const resetPageTrackingState = () => {
  lastTrackedPath = '';
  lastTrackedTime = 0;
};
